import * as Automerge from "@automerge/automerge";
import {
  isCanonicalSessionFeedDocument,
  readSessionFeedSnapshot,
} from "./feed";
import type {
  WSClientOptions,
  WSDisconnectInfo,
  WSReconnectErrorInfo,
  RPCRequest,
  RPCResponse,
  SnapshotResetMessage,
  RPCRequestFromServer,
} from "./types";

// Use global WebSocket if available (Browser, Bun, Deno)
let GlobalWebSocket: any = undefined;
if (typeof globalThis !== "undefined" && (globalThis as any).WebSocket) {
  GlobalWebSocket = (globalThis as any).WebSocket;
}

// WebSocket.OPEN is always 1
const READY_STATE_OPEN = 1;
const TOKEN_REFRESH_LEEWAY_MS = 2 * 60 * 1000;
const TOKEN_REFRESH_RETRY_MS = 30 * 1000;
const MAX_TIMER_DELAY_MS = 2_147_483_647;
const DEBUG_WS = process.env.GRANULAR_DEBUG_WS === "1";
const DEFAULT_RPC_TIMEOUT_MS = 30_000;
const DOMAIN_PACKAGE_RPC_TIMEOUT_MS = 120_000;
const EFFECT_CONTROL_RPC_TIMEOUT_MS = 120_000;
const HARNESS_RUN_RPC_TIMEOUT_MS = 600_000;
const HEARTBEAT_RPC_TIMEOUT_MS = 15_000;
const DEFAULT_CONNECT_TIMEOUT_MS = 10_000;
const DEFAULT_RECONNECT_DELAY_MS = 3_000;
const DEFAULT_MAX_RECONNECT_ATTEMPTS = 5;
const DOCUMENT_RESYNC_CLOSE_CODE = 4000;
const DOCUMENT_RESYNC_CLOSE_REASON = "Document resync required";

interface DocumentVersion {
  epoch: number;
  revision: number;
}

interface CanonicalDocumentQuarantine {
  documentEpoch: number;
  documentRevision: number;
  error: Error;
}

function documentVersion(
  document: Automerge.Doc<Record<string, unknown>>,
): DocumentVersion {
  const record = document as Record<string, unknown>;
  const epochValue = record.documentEpoch;
  const revisionValue = record.documentRevision;
  const epoch = epochValue === undefined ? 0 : epochValue;
  const revision = revisionValue === undefined ? 0 : revisionValue;
  if (
    !Number.isSafeInteger(epoch) ||
    Number(epoch) < 0 ||
    !Number.isSafeInteger(revision) ||
    Number(revision) < 0
  ) {
    throw new Error("Session document version metadata is invalid");
  }
  return {
    epoch: Number(epoch),
    revision: Number(revision),
  };
}

function stableDocumentFingerprint(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return '"[undefined]"';
  if (typeof value !== "object") return JSON.stringify(value) ?? String(value);
  if (value instanceof Date) return `date:${value.toISOString()}`;
  if (value instanceof Uint8Array) {
    return `bytes:${Array.from(value).join(",")}`;
  }
  if (Array.isArray(value)) {
    return `[${value.map(stableDocumentFingerprint).join(",")}]`;
  }
  const record = value as Record<string, unknown>;
  return `{${Object.keys(record)
    .sort()
    .map(
      (key) =>
        `${JSON.stringify(key)}:${stableDocumentFingerprint(record[key])}`,
    )
    .join(",")}}`;
}

function debugWs(...args: unknown[]): void {
  if (DEBUG_WS) {
    console.log(...args);
  }
}

function rpcTimeoutMsForMethod(method: string): number {
  switch (method) {
    case "domain.fetchPackagePart":
    case "domain.getSummary":
      return DOMAIN_PACKAGE_RPC_TIMEOUT_MS;
    case "client.heartbeat":
      return HEARTBEAT_RPC_TIMEOUT_MS;
    case "effects.publishCatalog":
    case "effects.resetCatalog":
    case "effects.addCatalog":
    case "effects.removeCatalog":
    case "effects.refresh":
    case "harness.prepare":
      return EFFECT_CONTROL_RPC_TIMEOUT_MS;
    case "harness.run":
      return HARNESS_RUN_RPC_TIMEOUT_MS;
    default:
      return DEFAULT_RPC_TIMEOUT_MS;
  }
}

export class WSClient {
  private ws: WebSocket | null = null;
  private url: string;
  private sessionId: string;
  private token: string;
  private messageQueue: Array<{
    resolve: (value: unknown) => void;
    reject: (error: Error) => void;
    id: string;
    timeout: ReturnType<typeof setTimeout>;
  }> = [];
  private rpcHandlers: Map<string, (params: unknown) => Promise<unknown>> =
    new Map();
  private eventHandlers: Map<string, Array<(params: unknown) => void>> =
    new Map();
  private canonicalDocumentQuarantineHandlers = new Set<
    (quarantine: CanonicalDocumentQuarantine) => void
  >();
  private nextRpcId = 1;
  public doc: Automerge.Doc<Record<string, unknown>> = Automerge.init();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private tokenRefreshTimer: ReturnType<typeof setTimeout> | null = null;
  private isExplicitlyDisconnected = false;
  private reconnectAttempts = 0;
  private connectPromise: Promise<void> | null = null;
  private connectionEpoch = 0;
  private cancelConnectAttempt: (() => void) | null = null;
  private documentRepairRequired = false;
  private canonicalDocumentActivated = false;

  private options: WSClientOptions;

  constructor(options: WSClientOptions) {
    this.options = options;
    this.url = options.url;
    this.sessionId = options.sessionId;
    this.token = options.token;
    if (options.initialDocumentSnapshot) {
      this.seedDocumentSnapshot(options.initialDocumentSnapshot);
    }
  }

  get currentSessionId(): string {
    return this.sessionId;
  }

  seedDocumentSnapshot(
    document: Record<string, unknown> | Uint8Array | null | undefined,
  ): void {
    if (!document || typeof document !== "object" || Array.isArray(document)) {
      return;
    }
    try {
      const replacement =
        document instanceof Uint8Array
          ? (Automerge.load(document) as Automerge.Doc<Record<string, unknown>>)
          : (Automerge.from(document) as Automerge.Doc<
              Record<string, unknown>
            >);
      if (!this.canAcceptDocumentReplacement(replacement, false)) {
        return;
      }
      this.doc = replacement;
      this.rememberCanonicalActivation(replacement);
      this.emit("sync", this.doc);
    } catch (error) {
      console.warn("[Granular] Failed to seed cached session document", error);
    }
  }

  saveDocumentSnapshot(): Uint8Array {
    return Automerge.save(this.doc);
  }

  private clearTokenRefreshTimer(): void {
    if (this.tokenRefreshTimer) {
      clearTimeout(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  private decodeBase64Url(payload: string): string {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4 || 4)) % 4);
    if (typeof atob === "function") {
      return atob(padded);
    }

    const maybeBuffer = (
      globalThis as unknown as {
        Buffer?: {
          from: (
            data: string,
            encoding: string,
          ) => { toString: (encoding: string) => string };
        };
      }
    ).Buffer;
    if (maybeBuffer) {
      return maybeBuffer.from(padded, "base64").toString("utf8");
    }

    throw new Error("No base64 decoder available");
  }

  private getTokenExpiryMs(token: string): number | null {
    const parts = token.split(".");
    if (parts.length < 2) {
      return null;
    }

    try {
      const payloadSegment = parts[1];
      if (!payloadSegment) {
        return null;
      }
      const payloadRaw = this.decodeBase64Url(payloadSegment);
      const payload = JSON.parse(payloadRaw) as { exp?: number };
      if (typeof payload.exp !== "number" || !Number.isFinite(payload.exp)) {
        return null;
      }
      return payload.exp * 1000;
    } catch {
      return null;
    }
  }

  private scheduleTokenRefresh(): void {
    this.clearTokenRefreshTimer();
    if (!this.options.tokenProvider || this.isExplicitlyDisconnected) {
      return;
    }

    const expiresAt = this.getTokenExpiryMs(this.token);
    if (!expiresAt) {
      return;
    }

    const refreshInMs = Math.max(
      1_000,
      expiresAt - Date.now() - TOKEN_REFRESH_LEEWAY_MS,
    );
    const delay = Math.min(refreshInMs, MAX_TIMER_DELAY_MS);
    this.tokenRefreshTimer = setTimeout(() => {
      void this.refreshTokenInBackground();
    }, delay);
  }

  private async refreshTokenInBackground(): Promise<void> {
    if (!this.options.tokenProvider || this.isExplicitlyDisconnected) {
      return;
    }

    try {
      const refreshedToken = await this.options.tokenProvider();
      if (typeof refreshedToken !== "string" || refreshedToken.length === 0) {
        throw new Error("Token provider returned no token");
      }
      this.token = refreshedToken;
      this.scheduleTokenRefresh();
    } catch (error) {
      if (this.isExplicitlyDisconnected) {
        return;
      }
      console.warn("[Granular] Token refresh failed, retrying soon:", error);
      this.clearTokenRefreshTimer();
      this.tokenRefreshTimer = setTimeout(() => {
        void this.refreshTokenInBackground();
      }, TOKEN_REFRESH_RETRY_MS);
    }
  }

  private async resolveTokenForConnect(): Promise<string> {
    if (!this.options.tokenProvider) {
      return this.token;
    }

    const expiresAt = this.getTokenExpiryMs(this.token);
    const shouldRefresh =
      expiresAt !== null && expiresAt - Date.now() <= TOKEN_REFRESH_LEEWAY_MS;
    if (!shouldRefresh) {
      return this.token;
    }

    try {
      const refreshedToken = await this.options.tokenProvider();
      if (typeof refreshedToken !== "string" || refreshedToken.length === 0) {
        throw new Error("Token provider returned no token");
      }
      this.token = refreshedToken;
      return refreshedToken;
    } catch (error) {
      if (expiresAt > Date.now()) {
        console.warn(
          "[Granular] Token refresh failed, using current token:",
          error,
        );
        return this.token;
      }
      throw error;
    }
  }

  /**
   * Connect to the WebSocket server
   * @returns {Promise<void>} Resolves when connection is open
   */
  async connect(options: { signal?: AbortSignal } = {}): Promise<void> {
    if (this.ws?.readyState === READY_STATE_OPEN) return;
    if (this.connectPromise) return this.connectPromise;

    const connectPromise = this.connectAttempt(options.signal);
    this.connectPromise = connectPromise;
    try {
      await connectPromise;
    } finally {
      if (this.connectPromise === connectPromise) {
        this.connectPromise = null;
      }
    }
  }

  /**
   * Mark the current transport as unusable and schedule the normal reconnect
   * path. Browser WebSockets can remain in OPEN state after a proxy/worker
   * restart, so a timed-out heartbeat must revoke the stale socket explicitly.
   */
  reportTransportFailure(reason: unknown = "WebSocket transport failed"): void {
    if (this.isExplicitlyDisconnected) return;

    const socket = this.ws;
    this.connectionEpoch += 1;
    this.ws = null;
    try {
      socket?.close(4001, "Transport failure");
    } catch {
      // The transport is already considered revoked; reconnect below is the
      // authoritative recovery action.
    }
    this.handleDisconnect({
      code: 4001,
      reason:
        reason instanceof Error ? reason.message : String(reason),
      wasClean: false,
    });
  }

  private async connectAttempt(signal?: AbortSignal): Promise<void> {
    if (signal?.aborted) throw new Error("WebSocket connect aborted");
    const token = await this.resolveTokenForConnect();
    if (signal?.aborted) throw new Error("WebSocket connect aborted");
    this.isExplicitlyDisconnected = false;
    this.scheduleTokenRefresh();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    let WebSocketClass = this.options.WebSocketCtor || GlobalWebSocket;
    if (!WebSocketClass) {
      try {
        // @ts-ignore - Optional Node transport.
        const wsModule = await import("ws");
        WebSocketClass = wsModule.default || wsModule;
      } catch {
        // Handled below.
      }
    }
    if (!WebSocketClass) {
      throw new Error(
        'No WebSocket implementation found. If using Node.js, please install "ws" and pass the constructor to the SDK options: { WebSocketCtor: WebSocket }.',
      );
    }

    const epoch = ++this.connectionEpoch;
    const wsUrl = new URL(this.url);
    wsUrl.searchParams.set("sessionId", this.sessionId);
    wsUrl.searchParams.set("token", token);
    // @ts-ignore - Dynamic browser/Node constructor.
    const socket = new WebSocketClass(wsUrl.toString()) as WebSocket;
    this.ws = socket;

    return new Promise<void>((resolve, reject) => {
      let settled = false;
      const configuredConnectTimeoutMs = this.options.connectTimeoutMs;
      const connectTimeoutMs =
        typeof configuredConnectTimeoutMs === "number" &&
        Number.isFinite(configuredConnectTimeoutMs) &&
        configuredConnectTimeoutMs > 0
          ? configuredConnectTimeoutMs
          : DEFAULT_CONNECT_TIMEOUT_MS;
      let connectTimeout: ReturnType<typeof setTimeout> | null = null;
      const isCurrent = () =>
        this.connectionEpoch === epoch && this.ws === socket;
      const finish = (error?: unknown) => {
        if (settled) return;
        settled = true;
        if (connectTimeout) {
          clearTimeout(connectTimeout);
          connectTimeout = null;
        }
        if (this.cancelConnectAttempt === handleAbort) {
          this.cancelConnectAttempt = null;
        }
        signal?.removeEventListener("abort", handleAbort);
        if (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
        } else {
          resolve();
        }
      };
      const closeStaleSocket = () => {
        try {
          socket.close(1000, "Stale connection attempt");
        } catch {
          // Best effort.
        }
      };
      const handleAbort = () => {
        if (isCurrent()) {
          this.connectionEpoch += 1;
          this.ws = null;
        }
        closeStaleSocket();
        finish(new Error("WebSocket connect aborted"));
      };
      this.cancelConnectAttempt = handleAbort;
      const handleOpen = () => {
        if (!isCurrent()) {
          closeStaleSocket();
          return;
        }
        this.reconnectAttempts = 0;
        this.emit("open", {});
        finish();
      };
      const handleMessage = (data: unknown) => {
        if (!isCurrent()) return;
        try {
          const text =
            typeof data === "string"
              ? data
              : data && typeof data === "object" && "toString" in data
                ? String(data.toString())
                : "";
          this.handleMessage(JSON.parse(text));
        } catch (error) {
          console.error("[Granular] Failed to parse message:", error);
        }
      };
      const handleError = (error: unknown) => {
        if (!isCurrent()) return;
        const typedError =
          error instanceof Error ? error : new Error("WebSocket error");
        this.emit("error", typedError);
        if (socket.readyState !== READY_STATE_OPEN) finish(typedError);
      };
      const handleClose = (close: {
        code?: number;
        reason?: unknown;
        wasClean?: boolean;
      }) => {
        if (!isCurrent()) return;
        if (!settled) {
          finish(
            new Error(
              `WebSocket closed before ready${close.code ? ` (code=${close.code})` : ""}`,
            ),
          );
        }
        this.handleDisconnect({
          code: close.code,
          reason: this.normalizeReason(close.reason),
          wasClean: close.wasClean,
        });
      };

      connectTimeout = setTimeout(() => {
        if (!isCurrent()) return;
        this.connectionEpoch += 1;
        this.ws = null;
        closeStaleSocket();
        finish(
          new Error(
            `WebSocket connect timed out after ${connectTimeoutMs}ms`,
          ),
        );
      }, connectTimeoutMs);

      signal?.addEventListener("abort", handleAbort, { once: true });
      const nodeSocket = socket as any;
      if (typeof nodeSocket.on === "function") {
        nodeSocket.on("open", handleOpen);
        nodeSocket.on("message", handleMessage);
        nodeSocket.on("error", handleError);
        nodeSocket.on("close", (code: number, reason: unknown) =>
          handleClose({ code, reason, wasClean: code === 1000 }),
        );
      } else {
        socket.onopen = handleOpen;
        socket.onmessage = (event: MessageEvent) => handleMessage(event.data);
        socket.onerror = handleError;
        socket.onclose = (event: CloseEvent) => handleClose(event);
      }
    });
  }

  private normalizeReason(reason: unknown): string | undefined {
    if (reason === null || reason === undefined) return undefined;
    if (typeof reason === "string") return reason;
    if (typeof reason === "object" && reason && "toString" in reason) {
      try {
        const text = String((reason as any).toString());
        return text || undefined;
      } catch {
        return undefined;
      }
    }
    return undefined;
  }

  private rejectPending(error: Error): void {
    this.messageQueue.forEach((pending) => {
      clearTimeout(pending.timeout);
      pending.reject(error);
    });
    this.messageQueue = [];
  }

  private emitReconnectErrorMessage(error: string, terminal = false): void {
    const reconnectInfo: WSReconnectErrorInfo = {
      error,
      sessionId: this.sessionId,
      timestamp: Date.now(),
      terminal,
    };
    this.emit("reconnect_error", reconnectInfo);
    if (this.options.onReconnectError) {
      try {
        this.options.onReconnectError(reconnectInfo);
      } catch (callbackError) {
        console.error(
          "[Granular] onReconnectError callback failed:",
          callbackError,
        );
      }
    }
  }

  private scheduleReconnectAttempt(): number | null {
    if (this.isExplicitlyDisconnected || this.reconnectTimer) return null;
    const baseReconnectDelayMs =
      typeof this.options.reconnectDelayMs === "number" &&
      Number.isFinite(this.options.reconnectDelayMs) &&
      this.options.reconnectDelayMs > 0
        ? this.options.reconnectDelayMs
        : DEFAULT_RECONNECT_DELAY_MS;
    const maxReconnectAttempts =
      typeof this.options.maxReconnectAttempts === "number" &&
      Number.isFinite(this.options.maxReconnectAttempts) &&
      this.options.maxReconnectAttempts >= 0
        ? Math.floor(this.options.maxReconnectAttempts)
        : DEFAULT_MAX_RECONNECT_ATTEMPTS;

    if (this.reconnectAttempts >= maxReconnectAttempts) {
      this.emitReconnectErrorMessage(
        `WebSocket reconnect attempts exhausted after ${maxReconnectAttempts} attempt(s).`,
        true,
      );
      return null;
    }

    this.reconnectAttempts += 1;
    const reconnectDelayMs = Math.min(
      30_000,
      baseReconnectDelayMs * 2 ** Math.max(0, this.reconnectAttempts - 1),
    );
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      console.log("[Granular] Attempting reconnect...");
      this.connect().catch((error) => {
        console.error("[Granular] Reconnect failed:", error);
        this.emitReconnectErrorMessage(
          error instanceof Error ? error.message : String(error),
        );
        // Socket-close failures may already have scheduled the next attempt.
        // Pre-socket failures (for example token refresh) arrive only here.
        this.scheduleReconnectAttempt();
      });
    }, reconnectDelayMs);
    return reconnectDelayMs;
  }

  private buildDisconnectError(info: WSDisconnectInfo): Error {
    const details = [
      info.code !== undefined ? `code=${info.code}` : undefined,
      info.reason ? `reason=${info.reason}` : undefined,
    ]
      .filter(Boolean)
      .join(", ");
    const suffix = details ? ` (${details})` : "";
    return new Error(`WebSocket disconnected${suffix}`);
  }

  private decodeDocumentBytes(
    payload: unknown,
    envelopeType: string,
  ): Uint8Array {
    if (typeof payload === "string") {
      const binaryString = atob(payload);
      const bytes = new Uint8Array(binaryString.length);
      for (let index = 0; index < binaryString.length; index += 1) {
        bytes[index] = binaryString.charCodeAt(index);
      }
      return bytes;
    }
    if (payload instanceof Uint8Array) {
      return payload;
    }
    if (
      Array.isArray(payload) &&
      payload.every(
        (value) =>
          typeof value === "number" &&
          Number.isInteger(value) &&
          value >= 0 &&
          value <= 255,
      )
    ) {
      return new Uint8Array(payload);
    }
    throw new Error(`${envelopeType} payload is not valid byte data`);
  }

  private rememberCanonicalActivation(
    document: Automerge.Doc<Record<string, unknown>>,
  ): void {
    if (isCanonicalSessionFeedDocument(document)) {
      this.canonicalDocumentActivated = true;
    }
  }

  private canAcceptDocumentReplacement(
    replacement: Automerge.Doc<Record<string, unknown>>,
    strictlyNewer: boolean,
  ): boolean {
    const replacementCanonical = isCanonicalSessionFeedDocument(replacement);
    const currentCanonical = isCanonicalSessionFeedDocument(this.doc);
    if (replacementCanonical) {
      // Activation is irreversible even when the first activated snapshot is
      // malformed. Latch the capability before validating the rest of the
      // document so a later non-canonical snapshot cannot deactivate the
      // canonical reader.
      this.canonicalDocumentActivated = true;
    }
    if (this.canonicalDocumentActivated && !replacementCanonical) {
      debugWs(
        "[Granular DEBUG] Rejected session document replacement that would deactivate the canonical feed.",
      );
      return false;
    }

    const current = documentVersion(this.doc);
    const incoming = documentVersion(replacement);
    if (incoming.epoch < current.epoch) {
      debugWs(
        `[Granular DEBUG] Rejected stale session document epoch ${incoming.epoch}; current epoch is ${current.epoch}.`,
      );
      return false;
    }
    if (incoming.epoch === current.epoch) {
      const minimumRevision = strictlyNewer
        ? current.revision + 1
        : current.revision;
      if (incoming.revision < minimumRevision) {
        debugWs(
          `[Granular DEBUG] Rejected stale session document revision ${incoming.revision}; current revision is ${current.revision}.`,
        );
        return false;
      }
    }
    if (replacementCanonical) {
      const incomingSnapshot = readSessionFeedSnapshot(replacement);
      if (incomingSnapshot.error) {
        this.documentRepairRequired = true;
        this.exposeCanonicalQuarantine(
          replacement,
          currentCanonical,
          incomingSnapshot.error,
        );
        debugWs(
          `[Granular DEBUG] Rejected malformed canonical snapshot: ${incomingSnapshot.error.message}`,
        );
        return false;
      }
    }
    const canonicalError = this.canonicalReplacementError(replacement);
    if (canonicalError) {
      debugWs(`[Granular DEBUG] Rejected session snapshot: ${canonicalError}`);
      return false;
    }
    if (
      this.canonicalDocumentActivated &&
      incoming.epoch === current.epoch &&
      incoming.revision === current.revision &&
      !readSessionFeedSnapshot(this.doc).error &&
      stableDocumentFingerprint(Automerge.toJS(replacement)) !==
        stableDocumentFingerprint(Automerge.toJS(this.doc))
    ) {
      debugWs(
        "[Granular DEBUG] Rejected divergent canonical snapshot at the accepted document version.",
      );
      return false;
    }
    return true;
  }

  private exposeCanonicalQuarantine(
    replacement: Automerge.Doc<Record<string, unknown>>,
    currentCanonical: boolean,
    error: Error,
  ): void {
    const version = documentVersion(replacement);
    if (currentCanonical) {
      // Never expose a rejected replacement through the ordinary sync event:
      // Session resource/tool consumers must remain on the accepted document.
      // The private, payload-free notice lets only the feed controller surface
      // the repair state while retaining accepted chronological history.
      const quarantine = Object.freeze({
        documentEpoch: version.epoch,
        documentRevision: version.revision,
        error: new Error(error.message),
      });
      for (const handler of this.canonicalDocumentQuarantineHandlers) {
        handler(quarantine);
      }
      return;
    }

    const accepted = Automerge.toJS(this.doc) as Record<string, unknown>;
    const quarantine = Automerge.from<Record<string, unknown>>({
      ...accepted,
      documentEpoch: version.epoch,
      documentRevision: version.revision,
      feed: {
        activation: { mode: "canonical" },
      },
    });
    this.doc = quarantine;
    this.emit("sync", this.doc);
    debugWs(
      `[Granular DEBUG] Canonical activation quarantined pending repair: ${error.message}`,
    );
  }

  private canonicalReplacementError(
    replacement: Automerge.Doc<Record<string, unknown>>,
  ): string | null {
    if (!isCanonicalSessionFeedDocument(replacement)) return null;
    const incoming = readSessionFeedSnapshot(replacement);
    if (incoming.error) {
      return `canonical feed is invalid: ${incoming.error.message}`;
    }
    if (!this.canonicalDocumentActivated) return null;

    const current = readSessionFeedSnapshot(this.doc);
    if (current.error) {
      // A valid full snapshot is allowed to repair a malformed cached seed.
      return null;
    }
    if (incoming.lastSequence < current.lastSequence) {
      return `canonical lastSequence ${incoming.lastSequence} regresses accepted ${current.lastSequence}`;
    }
    const currentBySequence = new Map(
      current.tail.map((item) => [item.sequence, item] as const),
    );
    for (const item of incoming.tail) {
      const accepted = currentBySequence.get(item.sequence);
      if (
        accepted &&
        stableDocumentFingerprint(item) !== stableDocumentFingerprint(accepted)
      ) {
        return `canonical occurrence ${item.sequence} conflicts with accepted history`;
      }
    }
    return null;
  }

  private assertIncrementalDocumentIsSafe(
    replacement: Automerge.Doc<Record<string, unknown>>,
  ): void {
    const missingDependencies = Automerge.getMissingDeps(replacement, []);
    if (missingDependencies.length > 0) {
      throw new Error(
        `Incremental session update is missing ${missingDependencies.length} causal dependency/dependencies`,
      );
    }

    if (
      this.canonicalDocumentActivated &&
      !isCanonicalSessionFeedDocument(replacement)
    ) {
      throw new Error(
        "Incremental session update would deactivate the canonical feed",
      );
    }

    const current = documentVersion(this.doc);
    const incoming = documentVersion(replacement);
    if (
      incoming.epoch < current.epoch ||
      (incoming.epoch === current.epoch && incoming.revision < current.revision)
    ) {
      throw new Error("Incremental session update regressed document version");
    }
    const canonicalError = this.canonicalReplacementError(replacement);
    if (canonicalError) {
      throw new Error(canonicalError);
    }
    if (
      this.canonicalDocumentActivated &&
      incoming.epoch === current.epoch &&
      incoming.revision === current.revision &&
      stableDocumentFingerprint(Automerge.toJS(replacement)) !==
        stableDocumentFingerprint(Automerge.toJS(this.doc))
    ) {
      throw new Error(
        "Incremental session update diverged without advancing document revision",
      );
    }
  }

  private requireDocumentResync(envelopeType: string, error: unknown): void {
    this.documentRepairRequired = true;
    console.warn(
      `[Granular] ${envelopeType} could not be applied; reconnecting for a fresh session snapshot.`,
      error,
    );

    const socket = this.ws;
    if (!socket) {
      this.scheduleReconnectAttempt();
      return;
    }

    // Immediately revoke ownership so no later frame from the suspect
    // transport can leak a partial document before the replacement snapshot.
    this.connectionEpoch += 1;
    this.ws = null;
    try {
      socket.close(DOCUMENT_RESYNC_CLOSE_CODE, DOCUMENT_RESYNC_CLOSE_REASON);
    } catch {
      // The transport is already revoked locally; reconnect still proceeds.
    }
    this.handleDisconnect({
      code: DOCUMENT_RESYNC_CLOSE_CODE,
      reason: DOCUMENT_RESYNC_CLOSE_REASON,
      wasClean: false,
    });
  }

  private handleDisconnect(
    close: {
      code?: number;
      reason?: string;
      wasClean?: boolean;
    } = {},
  ): void {
    const unexpected = !this.isExplicitlyDisconnected;
    const info: WSDisconnectInfo = {
      code: close.code,
      reason: close.reason,
      wasClean: close.wasClean,
      unexpected,
      timestamp: Date.now(),
      reconnectScheduled: false,
    };

    this.ws = null;
    this.emit("close", info);

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (unexpected) {
      const disconnectError = this.buildDisconnectError(info);
      const reconnectDelayMs = this.scheduleReconnectAttempt();
      info.reconnectScheduled = reconnectDelayMs !== null;
      if (reconnectDelayMs !== null) info.reconnectDelayMs = reconnectDelayMs;
      this.rejectPending(disconnectError);
      this.emit("disconnect", info);

      if (this.options.onUnexpectedClose) {
        try {
          this.options.onUnexpectedClose(info);
        } catch (callbackError) {
          console.error(
            "[Granular] onUnexpectedClose callback failed:",
            callbackError,
          );
        }
      }
    }
  }

  private handleMessage(message: unknown): void {
    if (typeof message !== "object" || message === null) return;

    // Debug: Log all incoming messages
    debugWs(
      "[Granular DEBUG] Received message:",
      JSON.stringify(message).slice(0, 500),
    );

    // The browser protocol has one incremental envelope: `change`.
    if ("type" in message && message.type === "sync") {
      this.requireDocumentResync(
        "Unsupported Automerge sync envelope",
        new Error("Use snapshot, snapshot_reset, or change."),
      );
      return;
    }

    // 1. Handling an epoch replacement after server-side checkpoint/rebase.
    if ("type" in message && message.type === "snapshot_reset") {
      const resetMessage = message as SnapshotResetMessage;
      try {
        if (
          !Number.isSafeInteger(resetMessage.documentEpoch) ||
          resetMessage.documentEpoch <= 0 ||
          !Number.isSafeInteger(resetMessage.documentRevision) ||
          resetMessage.documentRevision < 0 ||
          !Array.isArray(resetMessage.data)
        ) {
          throw new Error("snapshot_reset metadata is invalid");
        }

        const replacement = Automerge.load<Record<string, unknown>>(
          this.decodeDocumentBytes(
            resetMessage.data,
            "Automerge snapshot_reset",
          ),
        );
        const replacementVersion = documentVersion(replacement);
        const replacementEpoch = replacementVersion.epoch;
        const replacementRevision = replacementVersion.revision;
        if (
          replacementEpoch !== resetMessage.documentEpoch ||
          replacementRevision !== resetMessage.documentRevision
        ) {
          throw new Error(
            "snapshot_reset metadata does not match the saved document",
          );
        }

        if (!this.canAcceptDocumentReplacement(replacement, true)) {
          if (this.documentRepairRequired) {
            this.requireDocumentResync(
              "Stale Automerge snapshot_reset during document repair",
              new Error("Replacement reset did not advance accepted state"),
            );
          }
          return;
        }

        this.doc = replacement;
        this.documentRepairRequired = false;
        this.rememberCanonicalActivation(replacement);
        this.emit("snapshot_reset", {
          documentEpoch: replacementEpoch,
          documentRevision: replacementRevision,
        });
        this.emit("sync", this.doc);
      } catch (error) {
        this.requireDocumentResync("Automerge snapshot_reset", error);
      }
      return;
    }

    // 1. Handling Automerge Snapshots (Full State)
    if ("type" in message && message.type === "snapshot") {
      const snapshotMessage = message as { type: string; data?: unknown };
      try {
        const bytes = this.decodeDocumentBytes(
          snapshotMessage.data,
          "Automerge snapshot",
        );
        debugWs(
          "[Granular DEBUG] Loading Automerge session snapshot bytes:",
          bytes.length,
        );
        const replacement = Automerge.load<Record<string, unknown>>(bytes);
        if (!this.canAcceptDocumentReplacement(replacement, false)) {
          if (this.documentRepairRequired) {
            this.requireDocumentResync(
              "Stale Automerge snapshot during document repair",
              new Error("Replacement snapshot regressed accepted state"),
            );
          }
          return;
        }
        this.doc = replacement;
        this.documentRepairRequired = false;
        this.rememberCanonicalActivation(replacement);
        this.emit("sync", this.doc);
        debugWs(
          "[Granular DEBUG] Automerge session snapshot loaded. Doc:",
          JSON.stringify(Automerge.toJS(this.doc)),
        );
      } catch (error) {
        this.requireDocumentResync("Automerge snapshot", error);
      }
      return;
    }

    // 1.5 Handle Automerge Changes (Raw)
    if ("type" in message && message.type === "change") {
      if (this.documentRepairRequired) {
        debugWs(
          "[Granular DEBUG] Ignoring raw change while a replacement snapshot is required.",
        );
        return;
      }
      const changeMessage = message as { type: string; data?: unknown };
      try {
        const bytes = this.decodeDocumentBytes(
          changeMessage.data,
          "Automerge change",
        );
        // console.log('[Granular DEBUG] Applying change bytes:', bytes.length);
        const [newDoc] = Automerge.applyChanges(this.doc, [bytes]);
        this.assertIncrementalDocumentIsSafe(newDoc);
        this.doc = newDoc;
        this.rememberCanonicalActivation(newDoc);
        this.emit("change", changeMessage);
        this.emit("sync", this.doc);
        // console.log('[Granular DEBUG] Valid change applied. Doc keys:', Object.keys(Automerge.toJS(this.doc)));
      } catch (error) {
        this.requireDocumentResync("Automerge change message", error);
      }
      return;
    }

    // 2. Handle RPC Responses (Results/Errors from calls we made)
    if (
      "type" in message &&
      (message.type === "rpc_result" || message.type === "rpc_error")
    ) {
      const response = message as RPCResponse;
      const pending = this.messageQueue.find((q) => q.id === response.id);
      if (pending) {
        clearTimeout(pending.timeout);
        if (response.type === "rpc_error") {
          pending.reject(
            new Error(
              `RPC error: ${response.error?.message || "Unknown error"}`,
            ),
          );
        } else {
          pending.resolve(response.result);
        }
        this.messageQueue = this.messageQueue.filter(
          (q) => q.id !== response.id,
        );
      }
      return;
    }

    // 3. Handle Incoming RPC Requests (Server calls us, e.g. effect.invoke)
    if (
      "type" in message &&
      message.type === "rpc" &&
      "method" in message &&
      "id" in message
    ) {
      const request = message as RPCRequestFromServer;
      this.handleIncomingRpc(request).catch((error) => {
        console.error("[Granular] Error handling incoming RPC:", error);
      });
      return;
    }

    // 4. Handle Generic Events
    if ("type" in message && typeof message.type === "string") {
      this.emit(message.type, message);
    }
  }

  /**
   * Make an RPC call to the server
   * @param {string} method - RPC method name
   * @param {unknown} params - Request parameters
   * @returns {Promise<unknown>} Response result
   * @throws {Error} If connection is closed or timeout occurs
   */
  async call(method: string, params: unknown): Promise<unknown> {
    if (!this.ws || this.ws.readyState !== READY_STATE_OPEN) {
      throw new Error("WebSocket not connected");
    }

    const id = `rpc-${this.nextRpcId++}`;
    const request: RPCRequest = {
      type: "rpc",
      method,
      params,
      id,
    };

    return new Promise((resolve, reject) => {
      const timeoutMs = rpcTimeoutMsForMethod(method);
      const timeout = setTimeout(() => {
        const pending = this.messageQueue.find((q) => q.id === id);
        if (pending) {
          this.messageQueue = this.messageQueue.filter((q) => q.id !== id);
          reject(new Error(`RPC timeout: ${method}`));
        }
      }, timeoutMs);
      this.messageQueue.push({ resolve, reject, id, timeout });

      try {
        this.ws!.send(JSON.stringify(request));
      } catch (error) {
        clearTimeout(timeout);
        this.messageQueue = this.messageQueue.filter((q) => q.id !== id);
        reject(error instanceof Error ? error : new Error(String(error)));
      }
    });
  }

  private async handleIncomingRpc(
    request: RPCRequestFromServer,
  ): Promise<void> {
    const handler = this.rpcHandlers.get(request.method);

    if (!handler) {
      const errorResponse = {
        type: "rpc_error",
        id: request.id,
        error: {
          code: -32601,
          message: `Method not found: ${request.method}`,
        },
      };
      this.ws?.send(JSON.stringify(errorResponse));
      return;
    }

    try {
      const result = await handler(request.params);

      // Reverse effect invocations usually resolve by returning a value directly.
      // Only tool-era compatibility flows manage their own response frames.
      if (request.method !== "tool.invoke") {
        const successResponse = {
          type: "rpc_result",
          id: request.id,
          result,
        };
        this.ws?.send(JSON.stringify(successResponse));
      }
    } catch (error) {
      const errorResponse = {
        type: "rpc_error",
        id: request.id,
        error: {
          code: -32000,
          message: error instanceof Error ? error.message : "Unknown error",
        },
      };
      this.ws?.send(JSON.stringify(errorResponse));
    }
  }

  /**
   * Subscribe to client events
   * @param {string} event - Event name
   * @param {Function} handler - Event handler
   */
  on(event: string, handler: (params: unknown) => void): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler);
  }

  /**
   * Subscribe to rejected canonical replacement metadata without exposing the
   * malformed document through the public sync stream.
   *
   * @internal Session uses this to quarantine only its feed projection.
   */
  onCanonicalDocumentQuarantine(
    handler: (quarantine: CanonicalDocumentQuarantine) => void,
  ): () => void {
    this.canonicalDocumentQuarantineHandlers.add(handler);
    return () => {
      this.canonicalDocumentQuarantineHandlers.delete(handler);
    };
  }

  /**
   * Register an RPC handler for incoming server requests
   * @param {string} method - RPC method name
   * @param {Function} handler - Handler function
   */
  registerRpcHandler(
    method: string,
    handler: (params: unknown) => Promise<unknown>,
  ): void {
    this.rpcHandlers.set(method, handler);
  }

  /**
   * Unsubscribe from client events
   * @param {string} event - Event name
   * @param {Function} handler - Handler to remove
   */
  off(event: string, handler: (params: unknown) => void): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      this.eventHandlers.set(
        event,
        handlers.filter((h) => h !== handler),
      );
    }
  }

  /**
   * Emit an event locally
   * @param {string} event - Event name
   * @param params - Event data
   */
  emit(event: string, params: unknown): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler(params));
    }
  }

  /**
   * Disconnect the WebSocket and clear state
   */
  disconnect(options: { reason?: string } = {}): void {
    this.isExplicitlyDisconnected = true;
    this.cancelConnectAttempt?.();
    this.cancelConnectAttempt = null;
    this.connectionEpoch += 1;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.clearTokenRefreshTimer();

    if (this.ws) {
      this.ws.close(1000, options.reason || "Client disconnect");
      this.ws = null;
    }
    this.rejectPending(new Error("Client explicitly disconnected"));
    this.rpcHandlers.clear();
    this.emit("disconnect", {});
  }
}
